import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaImage } from 'react-icons/fa';
import './FuturisticAdmin.css';

const FuturisticAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    link: '',
    date: '',
    order: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const projectsRef = collection(db, 'futiristic');
      const snapshot = await getDocs(projectsRef);
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      projectsData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setProjects(projectsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    
    // Create previews
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const uploadImages = async () => {
    const uploadPromises = imageFiles.map(async (file) => {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `BIM/${fileName}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    
    return await Promise.all(uploadPromises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrls = editingProject?.images || [];
      
      // Upload new images if any
      if (imageFiles.length > 0) {
        const newImageUrls = await uploadImages();
        imageUrls = [...imageUrls, ...newImageUrls];
      }

      const projectData = {
        ...formData,
        images: imageUrls,
        date: formData.date ? new Date(formData.date) : new Date(),
        order: parseInt(formData.order) || 0
      };

      if (editingProject) {
        // Update existing project
        await updateDoc(doc(db, 'futiristic', editingProject.id), projectData);
      } else {
        // Add new project
        await addDoc(collection(db, 'futiristic'), projectData);
      }

      resetForm();
      fetchProjects();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      category: project.category || '',
      description: project.description || '',
      link: project.link || '',
      date: project.date ? new Date(project.date.seconds * 1000).toISOString().split('T')[0] : '',
      order: project.order || 0
    });
    setImagePreviews(project.images || []);
    setShowModal(true);
  };

  const handleDelete = async (projectId, images) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    setLoading(true);
    try {
      // Delete images from storage
      if (images && images.length > 0) {
        for (const imageUrl of images) {
          try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
          } catch (error) {
            console.error('Error deleting image:', error);
          }
        }
      }

      // Delete project document
      await deleteDoc(doc(db, 'futiristic', projectId));
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (index) => {
    if (editingProject) {
      const updatedImages = [...editingProject.images];
      updatedImages.splice(index, 1);
      setEditingProject({ ...editingProject, images: updatedImages });
      setImagePreviews(updatedImages);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      link: '',
      date: '',
      order: 0
    });
    setImageFiles([]);
    setImagePreviews([]);
    setEditingProject(null);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading && projects.length === 0) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="futuristic-admin">
      <div className="admin-header">
        <h1>Manage Futuristic Projects</h1>
        <button className="btn-add" onClick={() => setShowModal(true)}>
          <FaPlus /> Add New Project
        </button>
      </div>

      <div className="projects-grid">
        {projects.map(project => (
          <div key={project.id} className="project-card">
            <div className="project-image">
              {project.images && project.images.length > 0 ? (
                <img src={project.images[0]} alt={project.name} />
              ) : (
                <div className="no-image">
                  <FaImage />
                </div>
              )}
            </div>
            <div className="project-content">
              <h3>{project.name}</h3>
              <p className="category">{project.category}</p>
              <p className="description">{project.description?.substring(0, 100)}...</p>
              <div className="project-meta">
                <span className="order">Order: {project.order}</span>
                {project.images && <span className="image-count">{project.images.length} images</span>}
              </div>
            </div>
            <div className="project-actions">
              <button className="btn-edit" onClick={() => handleEdit(project)}>
                <FaEdit /> Edit
              </button>
              <button className="btn-delete" onClick={() => handleDelete(project.id, project.images)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
              <button className="btn-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="project-form">
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter project name"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Exhibition BIM"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Enter project description"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Link (Optional)</label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Images</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
                <div className="image-previews">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview">
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        className="btn-remove-image"
                        onClick={() => removeImage(index)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading ? 'Saving...' : (editingProject ? 'Update Project' : 'Add Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuturisticAdmin;